import moment, { type Moment } from 'moment';
import React, { useEffect, useMemo } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import NoticeForm from './NoticeForm';
import Loading from 'components/common/loading/Loading';
import BackButton from 'components/common/button/BackButton';

import useTitle from 'hooks/useTitle';
import { useFetchNoticeByIdQuery } from 'hooks/query/useNoticeQueries';

import history from 'utils/history';

import * as routes from 'constants/routes';

type NoticeRouteParams = {
  id?: string;
};

type NoticeRecord = {
  id: number;
  title: string;
  description: string | null;
  redirectUrl: string | null;
  startDate: Moment;
  endDate: Moment;
};

type NoticeByIdResponse = {
  data: Omit<NoticeRecord, 'startDate' | 'endDate'> & { startDate: string; endDate: string };
};

const CreateEditNotice: React.FC<RouteComponentProps<NoticeRouteParams>> = (props) => {
  const noticeId = props.match.params?.id;
  const title = noticeId ? 'Edit Notice' : 'Add Notice';
  const setTitle = useTitle();

  useEffect(() => {
    setTitle(title);
  }, [title, setTitle]);

  const {
    data: response,
    isLoading,
    isError,
  } = useFetchNoticeByIdQuery<NoticeByIdResponse>(noticeId, Boolean(noticeId));

  useEffect(() => {
    if (isError) {
      history.push(routes.NOTICES);
    }
  }, [isError]);

  const notice = useMemo<NoticeRecord | null>(() => {
    if (!response?.data) {
      return null;
    }

    const { data } = response;

    return {
      ...data,
      description: data.description ?? null,
      redirectUrl: data.redirectUrl ?? null,
      startDate: moment(data.startDate),
      endDate: moment(data.endDate),
    };
  }, [response]);

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div className="d-flex flex-row">
          <BackButton />
          <div className="d-flex flex-row">
            <div className="table-header">
              <h3 className="text-bold">{title}</h3>
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="loading-min-height">
          <Loading />
        </div>
      ) : (
        <NoticeForm notice={notice} />
      )}
    </div>
  );
};

export default CreateEditNotice;
